import React, { useMemo, useState } from 'react';

import AddCommentIcon from '@mui/icons-material/AddComment';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  Grow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment-timezone';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { useAuth } from '../AuthContext';
import FilesOpener from '../CGForm/FilesOpener';
import FirebaseUploadComponent from '../CreateCotation/FirebaseUploadComponent';
import FileCarouselDialog from '../FileCarouselDialog';
import CommentsDrawer from './CommentsDrawer';

interface InternalComment {
  user: string;
  date: string;
  comment: string;
  files?: string[];
}

interface InternalCommentsDrawerProps {
  open: boolean;
  onClose: () => void;
  registrationId: number;
  comments: InternalComment[];
  refetch: () => Promise<void>;
  type: 'cg' | 'da';
  onCommentAdded?: () => Promise<void>;
  regPlate?: string;
}

const InternalCommentsDrawer: React.FC<InternalCommentsDrawerProps> = ({
  open,
  onClose,
  registrationId,
  comments,
  refetch,
  type,
  onCommentAdded,
  regPlate,
}) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const theme = useTheme();

  const canAddComment = userRole === 'god' || userRole === 'arbitrator';

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState<boolean>(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const allCarouselFiles = useMemo(() => {
    const urls: string[] = [];
    comments.forEach((comment) => {
      comment.files?.forEach((u) => {
        if (u && !urls.includes(u)) urls.push(u);
      });
    });
    return urls;
  }, [comments]);

  const handleCarouselOpen = (url: string) => {
    const index = allCarouselFiles.indexOf(url);
    setCarouselIndex(index >= 0 ? index : 0);
    setCarouselOpen(true);
  };

  // Fonction pour gérer l'upload de fichiers
  const handleUploadComplete = (url: string) => {
    setUploadedUrls((prevUrls) => [...prevUrls, url]);
  };

  // Fonction pour gérer la suppression de fichiers
  const handleDeleteComplete = (url: string) => {
    setUploadedUrls((prevUrls) => prevUrls.filter((prevUrl) => prevUrl !== url));
  };

  const handleAddComment = async () => {
    // Permettre l'envoi si il y a du texte OU au moins un fichier
    if ((!newComment.trim() && uploadedUrls.length === 0) || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const endpoint = type === 'cg' ? '/api/cg/comments' : '/api/da/comments';
      await makeAuthenticatedApiCall('post', endpoint, {
        registrationId,
        comment: newComment,
        files: uploadedUrls, // Inclure les fichiers uploadés
      });
      setNewComment('');
      setUploadedUrls([]); // Réinitialiser les fichiers
      await refetch();

      // Appeler le callback pour rafraîchir les données dans le parent si nécessaire
      if (onCommentAdded) {
        await onCommentAdded();
      }

      // Fermer le drawer après l'ajout réussi du commentaire
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", JSON.stringify(error, null, 2));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCommentDate = (dateString: string) => {
    return moment(dateString).tz('Europe/Paris').format('DD/MM/YYYY à HH:mm');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      '#1976d2',
      '#388e3c',
      '#f57c00',
      '#d32f2f',
      '#7b1fa2',
      '#303f9f',
      '#5d4037',
      '#455a64',
      '#e91e63',
      '#00796b',
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  return (
    <>
    <CommentsDrawer
      open={open}
      onClose={onClose}
      title={`Commentaires internes (${comments.length})${regPlate ? ` - ${regPlate}` : ''}`}
      width={520}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Formulaire d'ajout de commentaire */}
        {canAddComment && (
          <Fade in={open} timeout={600}>
            <Card
              elevation={2}
              sx={{
                background: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8e9 100%)',
                border: '1px solid #e0e0e0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AddCommentIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Ajouter un commentaire interne
                  </Typography>
                </Box>

                <TextField
                  label="Votre commentaire"
                  variant="outlined"
                  multiline
                  rows={3}
                  fullWidth
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={
                    uploadedUrls.length > 0
                      ? 'Commentaire optionnel (fichiers déjà ajoutés)'
                      : 'Saisissez votre commentaire ici...'
                  }
                  sx={{
                    mb: 2,
                    '& .MuiInputLabel-root': {
                      fontWeight: 'normal !important',
                    },
                    '& .MuiInputBase-input': {
                      fontWeight: 'normal !important',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontWeight: 'normal !important',
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                />

                {/* Composant d'upload de fichiers */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Ajouter des fichiers (optionnel)
                  </Typography>
                  <FirebaseUploadComponent
                    onUploadComplete={handleUploadComplete}
                    onDeleteComplete={handleDeleteComplete}
                    uploadedUrls={uploadedUrls}
                    source={currentUser?.uid}
                  />
                </Box>

                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={(!newComment.trim() && uploadedUrls.length === 0) || isSubmitting}
                  startIcon={<SendIcon />}
                  className="dealActionButton"
                  sx={{
                    px: '24px !important',
                    py: 1,
                    fontWeight: 600,
                  }}
                >
                  {isSubmitting ? 'Envoi...' : 'Envoyer le commentaire'}
                </Button>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Liste des commentaires */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {comments.length === 0 ? (
            <Fade in={open} timeout={800}>
              <Card elevation={1} sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  Aucun commentaire interne pour le moment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {canAddComment ? 'Soyez le premier à ajouter un commentaire !' : 'Les commentaires apparaîtront ici.'}
                </Typography>
              </Card>
            </Fade>
          ) : (
            comments.map((comment, index) => (
              <Grow key={index} in={open} timeout={600 + index * 100} style={{ transformOrigin: '0 0 0' }}>
                <Card
                  elevation={1}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      elevation: 3,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header du commentaire */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(comment.user),
                          width: 40,
                          height: 40,
                          mr: 2,
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(comment.user)}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {comment.user}
                          </Typography>
                          <Chip
                            label="Skipcar"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatCommentDate(comment.date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Contenu du commentaire */}
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mb: 2,
                      }}
                    >
                      {comment.comment}
                    </Typography>

                    {/* Affichage des fichiers si présents */}
                    {comment.files && comment.files.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                          📎 Fichiers joints :
                        </Typography>
                        <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={comment.files.join(',')} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grow>
            ))
          )}
        </Box>
      </Box>
    </CommentsDrawer>

    <FileCarouselDialog
      allFiles={allCarouselFiles}
      initialIndex={carouselIndex}
      open={carouselOpen}
      onClose={() => setCarouselOpen(false)}
    />
  </>
  );
};

export default InternalCommentsDrawer;
